/**
 * A coarse, hand-authored climate model for the whole planet.
 *
 * The world streams from keyless map sources, and none of them carries climate:
 * a desert tile and a meadow tile arrive as the same elevation and the same
 * vector layers. So the look of a place has to be derived from *where* it is.
 * This module does that from two fields:
 *
 * - **Temperature** — a mean annual figure from latitude, cooled by 6.5 °C per
 *   kilometre of elevation, and nudged by a few large anomalies: the Gulf
 *   Stream warming north-west Europe, the continental cold of Siberia and
 *   interior Canada, the Greenland and Antarctic ice.
 * - **Aridity** — 0 soaking wet to 1 hyper-arid. A latitude baseline for the
 *   Hadley cells (wet equator, dry subtropics), overridden by soft regions for
 *   the deserts, steppes, savannas and rainforests people would recognise.
 *
 * Every region fades out over its outer two-thirds, and every downstream use blends
 * by weight rather than switching on a class, so crossing from the Sahel into
 * the Sahara is a gradient across hundreds of kilometres — never a seam at a
 * tile edge. It is not a climatology: it is tuned so that the places a player
 * is likely to type into a URL look like themselves.
 */

export interface Climate {
  /** Mean annual temperature at the given elevation, °C. */
  temperature: number;
  /** 0 wet .. 1 hyper-arid. */
  aridity: number;
  /** 0 .. 1, under a permanent ice sheet. */
  ice: number;
  /** 0 .. 1, winter-wet summer-dry coast: olives, cypress, terracotta. */
  mediterranean: number;
  /** True in the Americas, which is where cacti grow wild. */
  americas: boolean;
  /**
   * Half the annual temperature range, °C: warmest month minus the mean.
   * Near zero on the equator, ~9 on the Swedish coast, over 20 in Siberia.
   */
  seasonality: number;
}

interface Region {
  lat: number;
  lon: number;
  /** Half-extents in degrees. */
  rLat: number;
  rLon: number;
  /** Aridity inside the region, or undefined to leave it alone. */
  aridity?: number;
  /** °C added inside the region. */
  warm?: number;
  ice?: number;
  mediterranean?: number;
  /** Multiplier on the seasonal swing: the sea damps it, continents widen it. */
  swing?: number;
}

/**
 * The regions. Ellipses in degrees, which is crude at high latitude but these
 * are drawn with generous margins and fade softly, so the error is in where a
 * gradient runs, not in what a place looks like.
 */
const REGIONS: Region[] = [
  // --- hot deserts ---------------------------------------------------------
  { lat: 23, lon: 2, rLat: 9, rLon: 19, aridity: 0.96 },    // western Sahara
  { lat: 26, lon: 26, rLat: 9, rLon: 12, aridity: 0.97 },   // Libyan & Egyptian Sahara
  { lat: 22, lon: 48, rLat: 8, rLon: 11, aridity: 0.93 },   // Arabian
  { lat: 32, lon: 57, rLat: 5, rLon: 8, aridity: 0.82 },    // Iranian plateau
  { lat: 27, lon: 71, rLat: 3.5, rLon: 4.5, aridity: 0.85 },// Thar
  { lat: -24, lon: 20, rLat: 6, rLon: 7, aridity: 0.78 },   // Kalahari
  { lat: -23, lon: 15, rLat: 6, rLon: 2.4, aridity: 0.97 }, // Namib
  { lat: -25, lon: 132, rLat: 8, rLon: 15, aridity: 0.86 }, // Australian interior
  { lat: -23, lon: -69.5, rLat: 7, rLon: 2, aridity: 0.98 },// Atacama
  { lat: 31.5, lon: -112, rLat: 5, rLon: 7, aridity: 0.84 },// Sonoran & Mojave
  { lat: 29, lon: -104.5, rLat: 4, rLon: 4, aridity: 0.74 },// Chihuahuan
  { lat: 7, lon: 46, rLat: 5, rLon: 6, aridity: 0.78 },     // Horn of Africa
  // --- cold deserts and steppes -------------------------------------------
  { lat: 39.5, lon: 83, rLat: 3, rLon: 8, aridity: 0.93 },  // Taklamakan
  { lat: 43, lon: 105, rLat: 4.5, rLon: 11, aridity: 0.82 },// Gobi
  { lat: 46, lon: 63, rLat: 6, rLon: 16, aridity: 0.62 },   // Kazakh steppe
  { lat: 40, lon: -116, rLat: 4, rLon: 4.5, aridity: 0.68 },// Great Basin
  { lat: 39, lon: -102, rLat: 7, rLon: 5, aridity: 0.48 },  // Great Plains
  { lat: -45, lon: -69, rLat: 7, rLon: 3.5, aridity: 0.64 },// Patagonian steppe
  { lat: 33, lon: 101, rLat: 5, rLon: 12, aridity: 0.55 },  // Tibetan plateau
  // --- savannas -------------------------------------------------------------
  { lat: 13, lon: 8, rLat: 4, rLon: 30, aridity: 0.52 },    // Sahel
  { lat: -2, lon: 36, rLat: 8, rLon: 6, aridity: 0.48 },    // East African plateau
  { lat: -17, lon: 27, rLat: 7, rLon: 11, aridity: 0.46 },  // Zambezi woodland
  { lat: -14, lon: -48, rLat: 7, rLon: 8, aridity: 0.44 },  // Cerrado
  { lat: -16, lon: 133, rLat: 4.5, rLon: 15, aridity: 0.5 },// Northern Australia
  { lat: 18, lon: 77, rLat: 5, rLon: 5, aridity: 0.46 },    // Deccan
  { lat: 8, lon: -66, rLat: 3, rLon: 6, aridity: 0.45 },    // Llanos
  // --- rainforests ---------------------------------------------------------
  { lat: -3, lon: -62, rLat: 10, rLon: 16, aridity: 0.06 }, // Amazon
  { lat: 0, lon: 21, rLat: 6, rLon: 11, aridity: 0.08 },    // Congo
  { lat: 2, lon: 110, rLat: 10, rLon: 22, aridity: 0.06 },  // Maritime Southeast Asia
  { lat: 12, lon: -85, rLat: 6, rLon: 8, aridity: 0.15 },   // Central America
  { lat: -4, lon: 145, rLat: 6, rLon: 8, aridity: 0.06 },   // New Guinea
  // --- Mediterranean climates ---------------------------------------------
  { lat: 39, lon: 15, rLat: 7, rLon: 22, aridity: 0.4, mediterranean: 1 },
  { lat: 36, lon: -120.5, rLat: 5, rLon: 3, aridity: 0.45, mediterranean: 1 },
  { lat: -33.5, lon: -71, rLat: 4, rLon: 2, aridity: 0.42, mediterranean: 1 },
  { lat: -33.8, lon: 19.5, rLat: 1.8, rLon: 2.5, aridity: 0.4, mediterranean: 1 },
  { lat: -32.5, lon: 117, rLat: 3.5, rLon: 4, aridity: 0.45, mediterranean: 1 },
  // --- temperature anomalies ----------------------------------------------
  { lat: 57, lon: 6, rLat: 14, rLon: 28, warm: 5, swing: 0.7 }, // Gulf Stream Europe
  { lat: 60, lon: 45, rLat: 8, rLon: 20, warm: 3.5 },       // European Russia
  { lat: 63, lon: 110, rLat: 12, rLon: 40, warm: -6, swing: 1.6 }, // Siberia
  { lat: 60, lon: -100, rLat: 10, rLon: 28, warm: -3.5, swing: 1.35 }, // Canadian interior
  { lat: 45, lon: 65, rLat: 10, rLon: 25, swing: 1.3 },     // Central Asia
  { lat: 40, lon: -95, rLat: 9, rLon: 18, swing: 1.2 },     // North American interior
  { lat: 72, lon: -42, rLat: 14, rLon: 24, warm: -5 },      // Greenland, coast and all
  { lat: 74, lon: -41, rLat: 9.5, rLon: 13, ice: 1, warm: -12 }, // Greenland ice sheet
  { lat: 12, lon: 20, rLat: 20, rLon: 45, warm: 1.5 },      // hot tropical deserts
];

/** Half the annual temperature range by absolute latitude, °C, before swing. */
const LATITUDE_SEASONALITY: Array<[number, number]> = [
  [0, 0.6], [10, 1.5], [20, 4], [30, 7], [40, 10], [50, 12], [60, 13.5], [70, 16], [80, 19], [90, 20],
];

/** Mean annual sea-level temperature by absolute latitude, °C. */
const LATITUDE_TEMPERATURE: Array<[number, number]> = [
  [0, 27], [10, 27], [20, 25], [30, 20], [40, 14], [50, 8], [60, 1.5], [70, -7], [80, -17], [90, -26],
];

function interpolate(table: Array<[number, number]>, x: number): number {
  if (x <= table[0][0]) return table[0][1];
  for (let i = 1; i < table.length; i++) {
    if (x <= table[i][0]) {
      const [x0, y0] = table[i - 1];
      const [x1, y1] = table[i];
      return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
    }
  }
  return table[table.length - 1][1];
}

export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** 1 in a region's inner third, fading smoothly to 0 at its edge. */
function influence(region: Region, lat: number, lon: number): number {
  let dLon = Math.abs(lon - region.lon);
  if (dLon > 180) dLon = 360 - dLon;
  const d = Math.hypot((lat - region.lat) / region.rLat, dLon / region.rLon);
  return 1 - smoothstep(0.35, 1, d);
}

/** The Hadley-cell baseline: wet at the equator, dry in the subtropics. */
function baselineAridity(lat: number): number {
  const a = Math.abs(lat);
  return 0.22 + 0.22 * Math.exp(-((a - 27) ** 2) / 90) - 0.12 * Math.exp(-(a ** 2) / 60);
}

/**
 * Lapse rate, °C per metre of elevation.
 *
 * Gentler than the free-air 6.5 °C/km: high plateaus heat their own air, and
 * the textbook figure puts Nairobi at 15 °C when it is nearer 19.
 */
const LAPSE = 0.0055;

/** Climate at sea level; `atElevation` applies the lapse rate. */
export function seaLevelClimate(lat: number, lon: number): Climate {
  let temperature = interpolate(LATITUDE_TEMPERATURE, Math.abs(lat));
  // The south polar plateau is far colder than the north's sea ice.
  if (lat < -60) temperature -= (Math.min(90, -lat) - 60) * 0.6;

  let ice = lat < -66 ? 1 : 0;
  let mediterranean = 0;
  let swing = 1;

  // Aridity is a weighted average of the regions over this point, not a chain
  // of overrides: applied in order, whichever region came last in the list
  // would win the overlap and the Sahel would cut a hard step into the Sahara.
  // The baseline keeps whatever weight the strongest region leaves over.
  let wetSum = 0;
  let wetWeight = 0;
  let strongest = 0;

  for (const region of REGIONS) {
    const w = influence(region, lat, lon);
    if (w <= 0) continue;
    if (region.aridity !== undefined) {
      wetSum += region.aridity * w;
      wetWeight += w;
      strongest = Math.max(strongest, w);
    }
    if (region.warm !== undefined) temperature += region.warm * w;
    if (region.ice !== undefined) ice = Math.max(ice, region.ice * w);
    if (region.mediterranean !== undefined) mediterranean = Math.max(mediterranean, region.mediterranean * w);
    if (region.swing !== undefined) swing *= 1 + (region.swing - 1) * w;
  }
  const base = Math.max(0.02, 1 - strongest);
  const aridity = (baselineAridity(lat) * base + wetSum) / (base + wetWeight);

  return {
    temperature,
    aridity: Math.min(1, Math.max(0, aridity)),
    ice,
    mediterranean,
    americas: lon < -30 && lon > -170,
    // Dry interiors swing wider than wet coasts at the same latitude.
    seasonality: interpolate(LATITUDE_SEASONALITY, Math.abs(lat)) * swing * (0.85 + 0.3 * aridity),
  };
}

export function atElevation(sea: Climate, elevation: number): Climate {
  return { ...sea, temperature: sea.temperature - LAPSE * Math.max(0, elevation) };
}

export function climateAt(lat: number, lon: number, elevation = 0): Climate {
  return atElevation(seaLevelClimate(lat, lon), elevation);
}
